import React, { useEffect, useState } from 'react';
import { paymentsAPI, membersAPI } from '../../utils/api';
import { formatDate, formatCurrency } from '../../utils/helpers';
import Modal from '../../components/Modal';
import ConfirmModal from '../../components/ConfirmModal';
import StatusBadge from '../../components/StatusBadge';
import LoadingSpinner from '../../components/LoadingSpinner';
import toast from 'react-hot-toast';

function Payments() {
  const [payments, setPayments] = useState([]);
  const [pending, setPending] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [filterStatus, setFilterStatus] = useState('');
  const [payModal, setPayModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  const [form, setForm] = useState({
    memberId: '', amount: '', amountPaid: '', paymentMode: 'Cash', notes: '', nextDueDate: ''
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [payRes, pendRes, memRes] = await Promise.all([
        paymentsAPI.getAll(filterStatus ? { status: filterStatus } : {}),
        paymentsAPI.getPending(),
        membersAPI.getAll()
      ]);
      setPayments(payRes.data);
      setPending(pendRes.data);
      setMembers(memRes.data);
    } catch {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [filterStatus]);

  const handleRecord = async e => {
    e.preventDefault();
    try {
      await paymentsAPI.record({
        ...form,
        amount: parseFloat(form.amount),
        amountPaid: parseFloat(form.amountPaid),
      });
      toast.success('Payment recorded!');
      setPayModal(false);
      setForm({ memberId: '', amount: '', amountPaid: '', paymentMode: 'Cash', notes: '', nextDueDate: '' });
      fetchData();
    } catch {
      toast.error('Failed to record payment');
    }
  };

  const handleDelete = async () => {
    try {
      await paymentsAPI.delete(deleteModal.id);
      toast.success('Payment deleted');
      setDeleteModal({ open: false, id: null });
      fetchData();
    } catch {
      toast.error('Delete failed');
    }
  };

  const totalToday = payments
    .filter(p => new Date(p.paidDate).toDateString() === new Date().toDateString())
    .reduce((s, p) => s + p.amountPaid, 0);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Payments</h2>
          <p className="text-gray-500 text-sm">Today's collection: {formatCurrency(totalToday)}</p>
        </div>
        <button onClick={() => setPayModal(true)} className="btn-primary">+ Record Payment</button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card text-center">
          <p className="text-2xl font-bold text-blue-600">{payments.length}</p>
          <p className="text-sm text-gray-500 mt-1">Total Transactions</p>
        </div>
        <div className="card text-center">
          <p className="text-2xl font-bold text-red-600">{pending.length}</p>
          <p className="text-sm text-gray-500 mt-1">Pending Dues</p>
        </div>
        <div className="card text-center">
          <p className="text-2xl font-bold text-green-600">{formatCurrency(totalToday)}</p>
          <p className="text-sm text-gray-500 mt-1">Today</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {['all', 'pending'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${activeTab === tab ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            {tab === 'all' ? 'All Payments' : `Pending Dues (${pending.length})`}
          </button>
        ))}
      </div>

      {activeTab === 'all' && (
        <div className="card p-0 overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <select className="input w-auto text-sm" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">All Status</option>
              <option>Paid</option>
              <option>Partial</option>
              <option>Due</option>
            </select>
          </div>
          {loading ? <LoadingSpinner /> : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="table-header">Member</th>
                    <th className="table-header">Amount</th>
                    <th className="table-header">Paid</th>
                    <th className="table-header">Balance</th>
                    <th className="table-header">Mode</th>
                    <th className="table-header">Date</th>
                    <th className="table-header">Status</th>
                    <th className="table-header">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {payments.map(p => (
                    <tr key={p._id} className="hover:bg-gray-50">
                      <td className="table-cell">
                        <p className="font-medium text-sm">{p.memberId?.name || 'N/A'}</p>
                        <p className="text-xs text-gray-400">{p.memberId?.memberId}</p>
                      </td>
                      <td className="table-cell font-medium">{formatCurrency(p.amount)}</td>
                      <td className="table-cell text-green-600 font-medium">{formatCurrency(p.amountPaid)}</td>
                      <td className="table-cell text-red-500">{formatCurrency(p.balanceRemaining)}</td>
                      <td className="table-cell">
                        <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{p.paymentMode}</span>
                      </td>
                      <td className="table-cell text-xs">{formatDate(p.paidDate)}</td>
                      <td className="table-cell"><StatusBadge status={p.status} /></td>
                      <td className="table-cell">
                        <button
                          onClick={() => setDeleteModal({ open: true, id: p._id })}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {payments.length === 0 && (
                <div className="text-center py-12 text-gray-400">No payment records found</div>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'pending' && (
        <div className="card">
          <h3 className="font-semibold text-gray-700 mb-4">Members with Pending Dues</h3>
          {pending.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-8">All members are up to date with payments!</p>
          ) : (
            <div className="space-y-3">
              {pending.map(({ member, lastPayment }) => (
                <div key={member._id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-red-50 border border-red-100 rounded-xl">
                  <div>
                    <p className="font-medium text-gray-800">{member.name}</p>
                    <p className="text-xs text-gray-500">{member.memberId} • {member.phone} • {member.plan}</p>
                    {lastPayment && (
                      <p className="text-xs text-orange-600 mt-1">
                        Last: {formatCurrency(lastPayment.amountPaid)} on {formatDate(lastPayment.paidDate)} ({lastPayment.status})
                      </p>
                    )}
                    {!lastPayment && <p className="text-xs text-red-600 mt-1">No payment recorded</p>}
                  </div>
                  <button
                    onClick={() => { setForm(f => ({ ...f, memberId: member._id })); setPayModal(true); }}
                    className="btn-primary text-sm whitespace-nowrap"
                  >
                    Record Payment
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Record Payment Modal */}
      <Modal isOpen={payModal} onClose={() => setPayModal(false)} title="Record Payment">
        <form onSubmit={handleRecord} className="space-y-4">
          <div>
            <label className="label">Member *</label>
            <select className="input" value={form.memberId} onChange={e => setForm(f => ({ ...f, memberId: e.target.value }))} required>
              <option value="">Select member</option>
              {members.map(m => (
                <option key={m._id} value={m._id}>{m.name} ({m.memberId})</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Total Amount (₹) *</label>
              <input className="input" type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0" required />
            </div>
            <div>
              <label className="label">Amount Paid (₹) *</label>
              <input className="input" type="number" value={form.amountPaid} onChange={e => setForm(f => ({ ...f, amountPaid: e.target.value }))} placeholder="0" required />
            </div>
          </div>
          {form.amount && form.amountPaid && (
            <div className={`p-3 rounded-lg text-sm ${parseFloat(form.amountPaid) >= parseFloat(form.amount) ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
              Balance remaining: {formatCurrency(Math.max(0, parseFloat(form.amount || 0) - parseFloat(form.amountPaid || 0)))}
            </div>
          )}
          <div>
            <label className="label">Payment Mode</label>
            <select className="input" value={form.paymentMode} onChange={e => setForm(f => ({ ...f, paymentMode: e.target.value }))}>
              <option>Cash</option>
              <option>UPI</option>
              <option>Online</option>
            </select>
          </div>
          <div>
            <label className="label">Next Due Date</label>
            <input className="input" type="date" value={form.nextDueDate} onChange={e => setForm(f => ({ ...f, nextDueDate: e.target.value }))} />
          </div>
          <div>
            <label className="label">Notes</label>
            <textarea className="input resize-none" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional notes" />
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={() => setPayModal(false)} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary">Record Payment</button>
          </div>
        </form>
      </Modal>

      <ConfirmModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, id: null })}
        onConfirm={handleDelete}
        title="Delete Payment"
        message="Are you sure you want to delete this payment record?"
      />
    </div>
  );
}

export default Payments;
